import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/aplicativos-iptv/one-x")({
  head: () => ({
    meta: [
      { title: "Como Baixar e Instalar o One X na Roku TV, Android TV, TV Box e Celular Android | P2 PRO" },
      { name: "description", content: "Guia completo de como baixar e instalar o aplicativo One X na Roku TV, Android TV, TV Box e celular. Tutorial passo a passo atualizado em 2026." },
    ],
  }),
  component: () => (
    <SEOPage
      category="Tutorial"
      title="Como Baixar e Instalar o One X na Roku TV, Android TV, TV Box e Celular Android"
      subtitle="One X: Aplicativo Compatível com Painel IPTV Pós Pago"
      introParagraphs={[
        "O One X é um aplicativo moderno e otimizado para oferecer uma experiência rápida, estável e intuitiva em diversos dispositivos. Compatível com celulares Android, TV Box, Android TV e Roku TV, o aplicativo foi desenvolvido para funcionar com sistemas profissionais de Painel IPTV Pós Pago.",
        "Neste tutorial você aprenderá como baixar e instalar o One X de forma simples e rápida."
      ]}
      sections={[
        {
          title: "Como Baixar o One X no Celular Android",
          type: "default",
          content: (
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground">Siga o passo a passo abaixo:</p>
              <div className="grid gap-4">
                {[
                  { step: 1, text: "Abra a Google Play Store em seu celular." },
                  { step: 2, text: "Na barra de pesquisa, digite: One X" },
                  { step: 3, text: "Localize o aplicativo oficial One X." },
                  { step: 4, text: "Clique em Instalar." },
                  { step: 5, text: "Aguarde o download e a instalação automática." },
                  { step: 6, text: "Abra o aplicativo e insira seus dados de acesso." },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/5">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-electric/20 text-electric flex items-center justify-center font-black">
                      {item.step}
                    </span>
                    <p className="pt-2 text-muted-foreground">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-electric font-bold italic">Pronto! Agora você já pode utilizar o One X em seu smartphone Android.</p>
            </div>
          )
        },
        {
          title: "Como Instalar o One X em Android TV e TV Box",
          type: "default",
          content: (
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground">O processo é muito simples:</p>
              <div className="grid gap-4">
                {[
                  { step: 1, text: "Abra a Play Store da sua Android TV ou TV Box." },
                  { step: 2, text: "Pesquise por: One X" },
                  { step: 3, text: "Selecione o aplicativo oficial." },
                  { step: 4, text: "Clique em Instalar." },
                  { step: 5, text: "Após a instalação, abra o aplicativo." },
                  { step: 6, text: "Informe seu usuário e senha." },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/5">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-electric/20 text-electric flex items-center justify-center font-black">
                      {item.step}
                    </span>
                    <p className="pt-2 text-muted-foreground">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-electric font-bold italic">Agora o One X está pronto para uso.</p>
            </div>
          )
        },
        {
          title: "Como Baixar o One X na Roku TV",
          type: "default",
          content: (
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground">Se você utiliza uma Roku TV, siga estas etapas:</p>
              <div className="grid gap-4">
                {[
                  { step: 1, text: "Acesse a tela inicial da Roku TV." },
                  { step: 2, text: "Entre em Streaming Channels." },
                  { step: 3, text: "Utilize a busca da loja Roku." },
                  { step: 4, text: "Digite: One X" },
                  { step: 5, text: "Selecione o aplicativo oficial." },
                  { step: 6, text: "Clique em Adicionar Canal." },
                  { step: 7, text: "Aguarde a instalação." },
                  { step: 8, text: "Abra o aplicativo e faça login." },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/5">
                    <span className="flex-shrink-0 w-10 h-10 rounded-full bg-electric/20 text-electric flex items-center justify-center font-black">
                      {item.step}
                    </span>
                    <p className="pt-2 text-muted-foreground">{item.text}</p>
                  </div>
                ))}
              </div>
              <p className="text-electric font-bold italic">Pronto! O One X já estará disponível em sua Roku TV.</p>
            </div>
          )
        },
        {
          title: "Dispositivos Compatíveis com o One X",
          type: "featured",
          content: (
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "Celulares Android",
                "Tablets Android",
                "Android TV",
                "TV Box",
                "Roku TV",
                "Google TV",
                "Smart TVs compatíveis"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-electric shrink-0" />
                  <span className="text-muted-foreground">{item}</span>
                </div>
              ))}
            </div>
          )
        },
        {
          title: "Vantagens do Aplicativo One X",
          type: "default",
          content: (
            <div className="space-y-4">
              <p className="text-muted-foreground">O One X foi desenvolvido para oferecer praticidade e desempenho. Principais benefícios:</p>
              <ul className="grid gap-3 list-none p-0">
                {[
                  "Interface moderna e intuitiva",
                  "Instalação rápida",
                  "Atualizações automáticas",
                  "Excelente desempenho",
                  "Compatibilidade com múltiplos dispositivos",
                  "Fácil configuração",
                  "Integração com Painel IPTV Pós Pago"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 m-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-electric shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )
        },
        {
          title: "One X Compatível com Painel IPTV Pós Pago",
          type: "default",
          content: (
            <div className="space-y-6 text-muted-foreground leading-relaxed">
              <p>
                O One X é totalmente compatível com estruturas profissionais de Painel IPTV Pós Pago, permitindo gerenciamento eficiente de acessos e excelente experiência para clientes finais.
              </p>
              <p>
                Revendedores que utilizam sistemas modernos precisam de aplicativos confiáveis e estáveis. Por isso o One X se tornou uma excelente opção para operações profissionais. Além do One X, nossa estrutura também oferece compatibilidade com outros aplicativos populares do mercado.
              </p>
            </div>
          )
        },
        {
          title: "Outros Aplicativos Compatíveis",
          type: "default",
          content: (
            <div className="space-y-4">
              <p className="text-muted-foreground">Trabalhamos com aplicativos desenvolvidos para oferecer estabilidade e facilidade de uso:</p>
              <ul className="grid gap-3 list-none p-0">
                {["P2PRO", "P2PLAY", "Prime Play", "One X"].map((item) => (
                  <li key={item} className="flex items-center gap-3 m-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-electric shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground mt-4">Todos integrados ao nosso sistema de Painel IPTV Pós Pago.</p>
            </div>
          )
        },
        {
          title: "Problemas para Encontrar o One X?",
          type: "default",
          content: (
            <div className="grid gap-4">
              {[
                "Verifique sua conexão com a internet.",
                "Atualize a Play Store.",
                "Reinicie o dispositivo.",
                "Atualize o sistema operacional.",
                "Entre em contato com o suporte."
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                  <span className="text-muted-foreground">{item}</span>
                </div>
              ))}
            </div>
          )
        }
      ]}
      benefits={[
        { title: "Interface Moderna", description: "Design intuitivo para qualquer tela." },
        { title: "Instalação Rápida", description: "Configuração em poucos minutos." },
        { title: "Estabilidade", description: "Conteúdo sem travamentos em 4K." }
      ]}
      howItWorks={[
        { title: "1. Baixe", description: "Acesse a loja do seu aparelho." },
        { title: "2. Instale", description: "Siga o passo a passo do guia." },
        { title: "3. Login", description: "Use seus dados do Painel Pós Pago." },
        { title: "4. Assista", description: "Aproveite todo o conteúdo." }
      ]}
      diferenciais={[
        { title: "Suporte Roku", description: "Disponível na loja oficial Roku." },
        { title: "Pós Pago", description: "Integrado à nossa estrutura." },
        { title: "Qualidade", description: "Imagem cristalina em HD e 4K." }
      ]}
      faq={[
        { question: "O One X está disponível na Play Store?", answer: "Sim. O aplicativo pode ser encontrado diretamente na Play Store dos dispositivos compatíveis." },
        { question: "O One X funciona na Roku TV?", answer: "Sim. O aplicativo possui compatibilidade com Roku TV." },
        { question: "Posso usar o One X em Android TV?", answer: "Sim. O aplicativo funciona perfeitamente em Android TV e TV Box." },
        { question: "O One X funciona com Painel IPTV Pós Pago?", answer: "Sim. O aplicativo é totalmente compatível com sistemas profissionais de Painel IPTV Pós Pago." },
        { question: "Quais aplicativos são compatíveis com o painel?", answer: "Atualmente trabalhamos com One X, P2PRO, P2PLAY e Prime Play." }
      ]}
      breadcrumbs={[
        { label: "Aplicativos", href: "/aplicativos-iptv" },
        { label: "One X", href: "/aplicativos-iptv/one-x" }
      ]}
      ctaTitle="🚀 Tenha Seu Próprio Painel IPTV Pós Pago"
      ctaDescription="Comece hoje mesmo com uma estrutura profissional pronta para revenda. Sistema Pós Pago, Aplicativos Próprios, Ativação Rápida e Suporte Especializado."
      ctaButtonText="QUERO TESTAR O PAINEL IPTV PÓS PAGO AGORA"
    />
  ),
});